import { useEffect, useMemo, useState } from 'react'
import { fetchLinks, type SiteLinkItem } from '../api/public'
import { fetchSiteChrome, type LinktreeChrome } from '../api/site-chrome'
import { usePageMeta } from '../hooks/usePageMeta'
import { schemaGraph, schemaLinktreeProfile, schemaOrganization, schemaWebSite } from '../lib/schema-org'

const defaultChrome: LinktreeChrome = {
  profileLabel: '@bnkr.ch',
  profileUrl: 'https://www.bnkr.ch',
  footerText: 'Bunker — Event und Musik-Label',
}

function linkBtnClass(link: SiteLinkItem, index: number): string {
  const hay = `${link.label} ${link.url}`.toLowerCase()
  if (index === 0) return 'lt-btn lt-btn--primary'
  if (hay.includes('instagram') || hay.includes('tiktok')) return 'lt-btn lt-btn--crt'
  return 'lt-btn'
}

function linkLabel(link: SiteLinkItem): string {
  return link.emoji ? `${link.emoji} ${link.label}` : link.label
}

export function LinktreePage() {
  const [links, setLinks] = useState<SiteLinkItem[]>([])
  const [chrome, setChrome] = useState<LinktreeChrome>(defaultChrome)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const id = 'theme-linktree'
    if (!document.getElementById(id)) {
      const el = document.createElement('link')
      el.id = id
      el.rel = 'stylesheet'
      el.href = '/theme-linktree.css?v=1'
      document.head.appendChild(el)
    }
  }, [])

  useEffect(() => {
    Promise.all([
      fetchLinks().then((d) => d.links),
      fetchSiteChrome()
        .then((d) => d.linktree)
        .catch(() => defaultChrome),
    ])
      .then(([linkItems, lt]) => {
        setLinks(linkItems)
        setChrome(lt)
      })
      .catch(() => setLinks([]))
      .finally(() => setLoading(false))
  }, [])

  const profileHref = chrome.profileUrl?.trim() || 'https://www.bnkr.ch'

  const jsonLd = useMemo(
    () =>
      schemaGraph(schemaOrganization(), schemaWebSite(), ...schemaLinktreeProfile({ chrome, links })),
    [chrome, links],
  )

  usePageMeta({
    title: chrome.profileLabel,
    description: chrome.footerText ?? 'BNKR — Links zu Events, Socials und Shop.',
    canonical: 'https://links.bnkr.ch/',
    jsonLd,
  })

  return (
    <div className="lt-page">
      <main className="lt-shell">
        <header className="lt-hero">
          <p className="lt-brand">
            [<span className="crt">BNKR</span>]
          </p>
          <a className="lt-profile" href={profileHref} rel="noopener noreferrer">
            {chrome.profileLabel}
          </a>
        </header>

        <nav className="lt-links" aria-label="Links">
          {loading && <p className="lt-loading">…</p>}
          {links.map((link, i) => (
            <a
              key={link.id}
              href={link.url}
              className={linkBtnClass(link, i)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {linkLabel(link)}
            </a>
          ))}
        </nav>

        {chrome.footerText ? <footer className="lt-footer">{chrome.footerText}</footer> : null}
      </main>
    </div>
  )
}
